import React, { useMemo } from 'react';
import { ColumnDefinition } from 'apps/sigma2-ui/src/components/ui/Table';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { RootCauseGraphVariableBarPair } from 'libs/end-to-end/src/root-cause-analysis';
import { ExplanatoryFactorsTableProps } from 'libs/end-to-end/src/explanatory-factors';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const getOddDescription = ({
  group,
  language,
}: {
  language: Phrases;
  group: RootCauseGraphVariableBarPair;
}): TranslatedContent => {
  const values = [group.reference_level, group.treatment_level];

  const odd = Math.max(...values) / Math.min(...values);

  if (Number.isNaN(odd)) {
    return language.dangerousUserInput({ value: '-' });
  }

  if (group.reference_level >= group.treatment_level) {
    return language.moreLikely({
      odd: odd.toFixed(1),
    });
  }

  return language.lessLikely({
    odd: odd.toFixed(1),
  });
};

export const ExplanatoryFactorsTable = ({
  selectedReferenceLevel,
  groups,
}: ExplanatoryFactorsTableProps) => {
  const language = useLanguage();
  type ExplanatoryFactorsTableRow = {
    selectedReferenceLevel: string;
    comparisonDimensionLevel: string;
    odd: TranslatedContent;
  };

  const items: ExplanatoryFactorsTableRow[] = useMemo(() => {
    return groups.map((level) => {
      return {
        selectedReferenceLevel,
        comparisonDimensionLevel: level.name,
        odd: getOddDescription({
          group: level,
          language,
        }),
      };
    });
  }, [groups, language, selectedReferenceLevel]);

  const columnDefinitions: ColumnDefinition<ExplanatoryFactorsTableRow>[] =
    useMemo(
      () => [
        {
          id: 'selectedReferenceLevel',
          header: () => null,
          cell: ({ item }) => {
            return (
              <S2Typography
                dangerouslyOverrideClassNameFn={(cn) => `${cn} font-normal`}
                variant="headline-7-alt"
                translatedContent={language.dangerousUserInput({
                  value: item.selectedReferenceLevel,
                })}
              />
            );
          },
          cellClassNames: () => 'border-r-1 border-gray-200',
        },
        {
          id: 'comparisonDimensionLevel',
          header: () => null,
          cell: ({ item }) => {
            return (
              <S2Typography
                dangerouslyOverrideClassNameFn={(cn) => `${cn} font-normal`}
                variant="headline-7-alt"
                translatedContent={language.dangerousUserInput({
                  value: item.comparisonDimensionLevel,
                })}
              />
            );
          },
          cellClassNames: () => 'border-r-1 border-gray-200',
        },
        {
          id: 'odd',
          header: () => null,
          cell: ({ item }) => {
            return (
              <S2Typography
                dangerouslyOverrideClassNameFn={(cn) => `${cn} font-normal`}
                variant="headline-7-alt"
                translatedContent={item.odd}
              />
            );
          },
          cellClassNames: () => 'border-r-1 border-gray-200',
        },
      ],
      [language],
    );

  return (
    <Table
      items={items}
      columnDefinitions={columnDefinitions}
      displayHeader={false}
      classNames={{ rows: 'border-1 border-gray-200 text-xs' }}
    />
  );
};
